import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { authService } from './services';
import Navbar from './components/Navbar';
import ProtectedRoute from './components/ProtectedRoute';
import SessionExpiredModal from './components/SessionExpiredModal';
import { FeedbackProvider } from './components/FeedbackProvider';
import './styles/App.css';

const Home = lazy(() => import(/* webpackChunkName: "page-home" */ './pages/Home'));
const Login = lazy(() => import(/* webpackChunkName: "page-login" */ './pages/Login'));
const Register = lazy(() => import(/* webpackChunkName: "page-register" */ './pages/Register'));
const ForgotPassword = lazy(() => import(/* webpackChunkName: "page-forgot-password" */ './pages/ForgotPassword'));
const Playlists = lazy(() => import(/* webpackChunkName: "page-playlists" */ './pages/Playlists'));
const PlaylistDetail = lazy(() => import(/* webpackChunkName: "page-playlist-detail" */ './pages/PlaylistDetail'));
const Prizes = lazy(() => import(/* webpackChunkName: "page-prizes" */ './pages/PrizesWithCart'));
const Profile = lazy(() => import(/* webpackChunkName: "page-profile" */ './pages/Profile'));
const MarshmallowHome = lazy(() => import(/* webpackChunkName: "page-marshmallow-home" */ './pages/MarshmallowHome'));
const MarshmallowWrite = lazy(() => import(/* webpackChunkName: "page-marshmallow-write" */ './pages/MarshmallowWrite'));
const MarshmallowList = lazy(() => import(/* webpackChunkName: "page-marshmallow-list" */ './pages/MarshmallowList'));
const MarshmallowAdmin = lazy(() => import(/* webpackChunkName: "page-marshmallow-admin" */ './pages/MarshmallowAdmin'));
const BotConfig = lazy(() => import(/* webpackChunkName: "page-bot-config" */ './pages/BotConfig'));
const AiTokenQuotaAdmin = lazy(() => import(/* webpackChunkName: "page-ai-token-quota-admin" */ './pages/AiTokenQuotaAdmin'));
const QQBotConfig = lazy(() => import(/* webpackChunkName: "page-qq-bot-config" */ './pages/QQBotConfig'));
const PermissionManagement = lazy(() => import(/* webpackChunkName: "page-permission-management" */ './pages/PermissionManagement'));
const BlindBoxStats = lazy(() => import(/* webpackChunkName: "page-blindbox-stats" */ './pages/BlindBoxStats'));
const BlindBoxOverlay = lazy(() => import(/* webpackChunkName: "overlay-blindbox" */ './pages/BlindBoxOverlay'));
const GiftScreenshot = lazy(() => import(/* webpackChunkName: "page-gift-screenshot" */ './pages/GiftScreenshot'));
const GiftRecords = lazy(() => import(/* webpackChunkName: "page-gift-records" */ './pages/GiftRecords'));
const GiftOverlay = lazy(() => import(/* webpackChunkName: "overlay-gifts" */ './pages/GiftOverlay'));
const GiftOverlaySettings = lazy(() => import(/* webpackChunkName: "page-gift-overlay-settings" */ './pages/GiftOverlaySettings'));
const AdminPoints = lazy(() => import(/* webpackChunkName: "page-admin-points" */ './pages/AdminPoints'));
const AdminPrizes = lazy(() => import(/* webpackChunkName: "page-admin-prizes" */ './pages/AdminPrizes'));
const AdminPrizeOrders = lazy(() => import(/* webpackChunkName: "page-admin-prize-orders" */ './pages/AdminPrizeOrders'));
const AdminMovieTickets = lazy(() => import(/* webpackChunkName: "page-admin-movie-tickets" */ './pages/AdminMovieTickets'));
const ImageTools = lazy(() => import(/* webpackChunkName: "page-image-tools" */ './pages/ImageTools'));

const RouteLoadingFallback = () => (
  <div className="loading" role="status" aria-live="polite">
    加载中...
  </div>
);

// Component to verify session validity on route change/app load
const AuthVerifier = () => {
  const location = useLocation();
  const [isSessionExpired, setIsSessionExpired] = React.useState(false);

  useEffect(() => {
    const handleAuthExpired = () => {
      setIsSessionExpired(true);
    };

    window.addEventListener('auth:expired', handleAuthExpired);

    return () => {
      window.removeEventListener('auth:expired', handleAuthExpired);
    };
  }, []);

  useEffect(() => {
    const verifySession = async () => {
      // Only verify if we have a token and we are not already on the login page
      if (authService.isAuthenticated() && location.pathname !== '/login') {
        try {
          await authService.getProfile();
        } catch (error) {
          // 401 errors are handled by the api interceptor in services/api.js
        }
      }
    };

    verifySession();
  }, [location.pathname]);

  const handleCloseModal = () => {
    setIsSessionExpired(false);
    // Clear local storage and redirect to login
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  return <SessionExpiredModal isOpen={isSessionExpired} onClose={handleCloseModal} />;
};

function App() {
  return (
    <Router>
      <AuthVerifier />
      <Routes>
        {/* OBS 透明浏览器源 - 无 Navbar */}
        <Route
          path="/live/blindbox/:roomId"
          element={
            <Suspense fallback={null}>
              <BlindBoxOverlay />
            </Suspense>
          }
        />
        <Route
          path="/live/gifts/:accessToken"
          element={
            <Suspense fallback={null}>
              <GiftOverlay />
            </Suspense>
          }
        />

        {/* 其他所有页面 - 有 Navbar */}
        <Route path="/*" element={
          <FeedbackProvider>
            <div className="App">
              <Navbar />
              <Suspense fallback={<RouteLoadingFallback />}>
                <Routes>
                <Route path="/" element={<Home />} />
                <Route
                  path="/tools/image"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'premium']}>
                      <ImageTools />
                    </ProtectedRoute>
                  }
                />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/playlists" element={<Playlists />} />
                <Route path="/playlists/:id" element={<PlaylistDetail />} />
                <Route path="/marshmallows" element={<MarshmallowHome />} />
                <Route path="/marshmallows/write" element={<MarshmallowWrite />} />
                <Route
                  path="/marshmallows/my"
                  element={
                    <ProtectedRoute>
                      <MarshmallowList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/marshmallows"
                  element={
                    <ProtectedRoute requiredPermissions={['marshmallow.manage']}>
                      <MarshmallowAdmin />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/bot"
                  element={
                    <ProtectedRoute requiredPermissions={['bot.admin', 'bot.room.view']}>
                      <BotConfig />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/bot/ai-tokens"
                  element={
                    <ProtectedRoute requiredPermissions={['bot.admin']}>
                      <AiTokenQuotaAdmin />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/bot/qq"
                  element={
                    <ProtectedRoute requiredPermissions={['bot.admin', 'bot.room.view']}>
                      <QQBotConfig />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/blindbox"
                  element={
                    <ProtectedRoute requiredPermissions={['bot.admin', 'bot.room.view']}>
                      <BlindBoxStats />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/gift-records"
                  element={
                    <ProtectedRoute>
                      <GiftRecords />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/gift-screenshot"
                  element={
                    <ProtectedRoute requiredPermissions={['bot.admin', 'bot.room.view']}>
                      <GiftScreenshot />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/gift-overlay"
                  element={
                    <ProtectedRoute requiredPermissions={['bot.admin', 'bot.room.view']}>
                      <GiftOverlaySettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/permissions"
                  element={
                    <ProtectedRoute adminOnly={true}>
                      <PermissionManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/points"
                  element={
                    <ProtectedRoute adminOnly={true}>
                      <AdminPoints />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/movie-tickets"
                  element={
                    <ProtectedRoute adminOnly={true}>
                      <AdminMovieTickets />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/prizes/new"
                  element={
                    <ProtectedRoute adminOnly={true}>
                      <AdminPrizes />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/prizes/:prizeId/edit"
                  element={
                    <ProtectedRoute adminOnly={true}>
                      <AdminPrizes />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/prizes"
                  element={
                    <ProtectedRoute adminOnly={true}>
                      <AdminPrizes />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/prize-orders"
                  element={
                    <ProtectedRoute adminOnly={true}>
                      <AdminPrizeOrders />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/prizes"
                  element={
                    <ProtectedRoute>
                      <Prizes />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  }
                />
                </Routes>
              </Suspense>
            </div>
          </FeedbackProvider>
        } />
      </Routes>
    </Router>
  );
}

export default App;
